import { _ } from "../../../core/polyglot/polyglot.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import FormulaWidget from "../../../core/widgets/keyFigures/FormulaWidget.js";
import KeyFigureChartOptionsEditorWidget from "./KeyFigureChartOptionsEditorWidget/KeyFigureChartOptionsEditorWidget.js";
import KeyFigureFormulaEditorWidget from "./KeyFigureFormulaEditorWidget.js";
import KeyFigureValueTypeEditorWidget from "./KeyFigureValueTypeEditorWidget.js";
import sectionWidget from "../../../core/widgets/sectionWidget.js";

/**
 * KeyFigureEditorWidget is the main widget for editing a key figure. It renders
 * sub-widgets for editing various part of a key figure: its formula, value
 * type, chart options, and comment.
 *
 * @param {keyFigure} spec.keyFigure - Key figure to be edited.
 * @param {Dimension} spec.dimensionTree - Tree of dimensions available.
 */
export default class KeyFigureEditorWidget extends Widget {
	constructor({ keyFigure, dimensionTree }) {
		super(...arguments);

		this._keyFigure = keyFigure;
		this._dimensionTree = dimensionTree;

		this.tokenSelected = this._events.createEvent();
		this.keyFigureChanged = this._events.createEvent();

		this._keyFigureValueTypeEditor = new KeyFigureValueTypeEditorWidget({
			keyFigure: this._keyFigure,
		});

		this._keyFigureValueTypeEditor.keyFigureValueTypeChanged.register(
			(valueType) => this._setKeyFigureValueType(valueType),
		);

		this._keyFigureFormulaViewer = new FormulaWidget({
			formulaNode: this._keyFigure.getFormula(),
			useAnchors: false,
			dimensionTree: this._dimensionTree,
		});

		this._formulaEditor = new KeyFigureFormulaEditorWidget({
			formula: this._keyFigure.getFormula(),
			dimensionTree: this._dimensionTree,
		});

		this._formulaEditor.tokenSelected.register(() =>
			this.tokenSelected.trigger(),
		);

		this._formulaEditor.formulaUpdated.register((formula) =>
			this._setFormula(formula),
		);

		this._keyFigureChartOptionsEditor =
			new KeyFigureChartOptionsEditorWidget({
				keyFigure: this._keyFigure,
			});

		this._keyFigureChartOptionsEditor.keyFigureChartOptionsChanged.register(
			() => this.keyFigureChanged.trigger(),
		);
	}

	getSelectedFormulaToken() {
		return this._formulaEditor.getSelectedToken();
	}

	updateFormula() {
		this._formulaEditor.updateFormula();
	}

	addFormulaToken(token) {
		this._formulaEditor.addToken(token);
	}

	deleteFormulaToken(token) {
		this._formulaEditor.deleteToken(token);
	}

	setKeyFigure(keyFigure, options) {
		this._keyFigure = keyFigure;
		this._keyFigureChartOptionsEditor.setKeyFigure(keyFigure);
		this._keyFigureValueTypeEditor.setKeyFigure(keyFigure);

		this._formulaEditor.setFormula(this._keyFigure.getFormula(options));
		this._keyFigureFormulaViewer.setFormula(this._keyFigure.getFormula());
	}

	setKeyFigureResult(keyFigureResult) {
		this._keyFigureValueTypeEditor.setResult(keyFigureResult);
		this._keyFigureChartOptionsEditor.setResult(keyFigureResult);
	}

	validate({ collector }) {
		this._formulaEditor.validate({ collector });
	}

	isFormulaValid() {
		return this._formulaEditor.isFormulaValid();
	}

	renderContentOn(html) {
		html.render(
			this._renderFormulaEditorOn.bind(this),
			this._sectionRenderer((html) => [
				html.div(
					{ klass: "col-md-4 col-sm-12" },
					this._keyFigureValueTypeEditor,
				),
				html.div(
					{ klass: "col-md-8 col-sm-12" },
					this._keyFigureChartOptionsEditor,
				),
			]),
			this._sectionRenderer((html) => [
				html.div(
					{ klass: "col-md-6 col-sm-12" },
					html.h2(_("Comment")),
					this._renderKeyFigureCommentEditorOn.bind(this),
				),
				html.div(
					{ klass: "col-md-6 col-sm-12" },
					html.h2(_("Preview")),
					html.div(
						{
							klass: "formula-preview",
							"data-test": "formula-preview",
						},
						this._keyFigureFormulaViewer,
					),
				),
			]),
		);
	}

	_sectionRenderer(content) {
		return sectionWidget({
			title: "",
			content: (html) => {
				html.div({ klass: "row" }, content);
			},
		});
	}

	_renderFormulaEditorOn(html) {
		if (!this._keyFigure.isFinancialKeyFigure()) {
			return;
		}

		html.render(
			this._sectionRenderer((html) =>
				html.div({ klass: "col-md-12" }, this._formulaEditor),
			),
		);
	}

	_renderKeyFigureCommentEditorOn(html) {
		html.textarea(
			{
				klass: "form-control kpi-comment",
				name: "comment",
				change: (event) => {
					this._setKeyFigureDescription(event.target.value);
				},
			},
			this._keyFigure.getDescription(),
		);
	}

	_setKeyFigureDescription(description) {
		this._keyFigure.setDescription(description);
	}

	_setFormula(formula) {
		this._keyFigure.setFormula(formula);
		this._keyFigureFormulaViewer.setFormula(formula);
		this.keyFigureChanged.trigger();
	}

	_setKeyFigureValueType(valueType) {
		this._keyFigure.setValueType(valueType);
		this.keyFigureChanged.trigger();
	}
}
